import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminShell from './AdminShell';
import { api } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import { Save, ArrowLeft, Loader2 } from 'lucide-react';
import CoverImagePanel, { HISTORY_LIMIT } from '../../components/admin/CoverImagePanel';

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');

const empty = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  author: 'BriefingIQ Team',
  cover_image: '',
  cover_history: [],
  alt_text: '',
  tags: [],
  published: true,
  featured: false,
  seo_title: '',
  seo_description: '',
};

const AdminBlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [tagsText, setTagsText] = useState('');

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const list = await api.get('/admin/blog');
        const p = list.data.find((x) => x.id === id);
        if (p) {
          setForm({ ...empty, ...p });
          setTagsText((p.tags || []).join(', '));
        }
      } catch (e) {
        // silent
      }
    })();
  }, [id]);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });
  const tags = tagsText.split(',').map((t) => t.trim()).filter(Boolean);

  const save = async () => {
    if (!form.title) {
      toast({ title: 'Title is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      slug: form.slug || slugify(form.title),
      tags,
      cover_history: (form.cover_history || []).slice(0, HISTORY_LIMIT),
    };
    try {
      if (id) {
        await api.put(`/admin/blog/${id}`, payload);
      } else {
        await api.post('/admin/blog', payload);
      }
      toast({ title: 'Saved' });
      navigate('/admin/blog');
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell title={id ? 'Edit post' : 'New post'}>
      <button onClick={() => navigate('/admin/blog')} className="btn-link mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-[1fr,320px] gap-6">
        <div className="bg-white rounded-2xl border border-[var(--line-2)] p-6 space-y-5">
          <CoverImagePanel
            value={form}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            context={{ title: form.title, excerpt: form.excerpt, tags }}
          />

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-ink-3">Title</Label>
            <Input value={form.title} onChange={change('title')} className="mt-1.5 rounded-lg border-[var(--line-2)]" placeholder="Post title" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-ink-3">Slug</Label>
            <Input value={form.slug} onChange={change('slug')} className="mt-1.5 rounded-lg border-[var(--line-2)]" placeholder="auto-from-title" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-ink-3">Excerpt</Label>
            <Textarea value={form.excerpt} onChange={change('excerpt')} rows={2} className="mt-1.5 rounded-lg border-[var(--line-2)]" placeholder="Short summary…" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-ink-3">Content (Markdown-lite)</Label>
            <Textarea value={form.content} onChange={change('content')} rows={18} className="mt-1.5 rounded-lg border-[var(--line-2)] font-mono text-sm" placeholder="Use ## for headings, - for lists, **bold**" />
          </div>
        </div>

        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-[var(--line-2)] p-6 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] uppercase tracking-wider text-ink-3">Published</Label>
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
            </div>
            <div className="flex items-center justify-between pt-1">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-ink-3">Featured</Label>
                <div className="text-[10px] text-ink-3 mt-0.5">Pins as the large card on the Blog page. Only one at a time.</div>
              </div>
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-ink-3">Author</Label>
              <Input value={form.author} onChange={change('author')} className="mt-1.5 rounded-lg border-[var(--line-2)]" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-ink-3">Tags (comma separated)</Label>
              <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} className="mt-1.5 rounded-lg border-[var(--line-2)]" placeholder="AI, Briefings" />
            </div>
            <button onClick={save} disabled={saving} className="btn-primary w-full justify-center">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save</>}
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-[var(--line-2)] p-6 space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-ink-3">SEO</div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-ink-3">SEO title</Label>
              <Input value={form.seo_title || ''} onChange={change('seo_title')} className="mt-1.5 rounded-lg border-[var(--line-2)]" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-ink-3">SEO description</Label>
              <Textarea value={form.seo_description || ''} onChange={change('seo_description')} rows={3} className="mt-1.5 rounded-lg border-[var(--line-2)]" />
            </div>
          </div>
        </aside>
      </div>
    </AdminShell>
  );
};

export default AdminBlogEditor;
